import React, { useState } from 'react';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';

import { makeStyles, fade } from '@material-ui/core/styles';
import { blueGrey, green, yellow, purple, indigo } from '@material-ui/core/colors';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const PESO_RECEBIMENTO = 20;
const PESO_DIGITALIZACAO = 20;
const PESO_DIGITACAO = 60;

const FASES = [
    { chave: 'recebimento', rotulo: 'Recebimento', peso: PESO_RECEBIMENTO, cor: purple[800] },
    { chave: 'digitalizacao', rotulo: 'Digitalização', peso: PESO_DIGITALIZACAO, cor: indigo[800] },
    { chave: 'digitacao', rotulo: 'Digitação', peso: PESO_DIGITACAO, cor: green[800] },
];

const clamp01 = valor => Math.min(Math.max(valor, 0), 1);

const listras = cor => `repeating-linear-gradient(45deg, ${cor} 0px, ${cor} 12px, ${fade(cor, 0.6)} 12px, ${fade(cor, 0.6)} 24px)`;

const useStyles = makeStyles(theme => ({
    card: {
        marginBottom: theme.spacing(2),
        boxShadow: theme.shadows[2],
    },
    cardVerde: {
        backgroundColor: fade(theme.palette.primary.main, 0.08),
        border: `1px solid ${theme.palette.primary.dark}`,
    },
    cardAmarelo: {
        backgroundColor: fade(yellow[700], 0.12),
        border: `1px solid ${yellow[900]}`,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    titulo: {
        color: blueGrey[800],
        fontWeight: 700,
    },
    percentual: {
        fontWeight: 700,
        lineHeight: 1.1,
        fontSize: '2.4rem',
    },
    percentualVerde: {
        color: green[700],
    },
    percentualAmarelo: {
        color: yellow[800],
    },
    contadores: {
        marginTop: theme.spacing(0.5),
        color: blueGrey[700],
    },
    faixa: {
        width: '100%',
        maxWidth: 864,
        marginTop: theme.spacing(1.5),
    },
    track: {
        position: 'relative',
        height: 14,
        borderRadius: 7,
        overflow: 'hidden',
        backgroundColor: theme.palette.grey[200],
    },
    preenchimento: {
        height: '100%',
        backgroundSize: '33.94px 33.94px',
        animation: '$deslizar 0.9s linear infinite',
        transition: theme.transitions.create('width'),
    },
    preenchimentoVerde: {
        backgroundImage: listras(green[700]),
    },
    preenchimentoAmarelo: {
        backgroundImage: listras(yellow[800]),
    },
    '@keyframes deslizar': {
        '0%': { backgroundPosition: '0 0' },
        '100%': { backgroundPosition: '33.94px 0' },
    },
    toggle: {
        marginTop: theme.spacing(1),
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.shape.borderRadius,
        color: blueGrey[700],
        '&:hover': {
            backgroundColor: fade(blueGrey[700], 0.08),
        },
    },
    chevron: {
        transition: theme.transitions.create('transform'),
    },
    chevronAberto: {
        transform: 'rotate(180deg)',
    },
    detalhamento: {
        width: '100%',
        maxWidth: 864,
    },
    etapas: {
        width: '100%',
        marginTop: theme.spacing(0.5),
        textAlign: 'left',
    },
    etapa: {
        marginBottom: theme.spacing(1.25),
    },
    etapaCabecalho: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
    },
    etapaNome: {
        flex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        color: blueGrey[800],
    },
    etapaPeso: {
        color: theme.palette.text.secondary,
    },
    etapaPercentual: {
        fontWeight: 700,
        color: blueGrey[800],
    },
    etapaTrack: {
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        backgroundColor: theme.palette.grey[200],
    },
    etapaFill: {
        height: '100%',
        backgroundSize: '33.94px 33.94px',
        animation: '$deslizar 0.9s linear infinite',
        transition: theme.transitions.create('width'),
    },
    ponto: {
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
    },
    detalhe: {
        marginTop: theme.spacing(1.5),
        maxWidth: 1250,
        color: theme.palette.text.secondary,
    },
}));

const formatarPercentual = valor =>
    `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export const ProgressoProcesso = ({ visaoGeral, regiao, incluiGrupo }) => {
    const classes = useStyles();
    const [ aberto, setAberto ] = useState(false);
    const dados = visaoGeral ?? {};

    const recebidas = dados.recebidas ?? 0;
    const digitalizadas = dados.digitalizadas ?? 0;
    const digitadas = dados.digitadas ?? 0;
    const descartadas = dados.descartadas ?? 0;

    const base = Math.max(recebidas - descartadas, 0);

    const razao = contagem => base > 0 ? clamp01(contagem / base) : 0;

    const conclusao = {
        recebimento: razao(recebidas),
        digitalizacao: razao(digitalizadas),
        digitacao: razao(digitadas),
    };

    const progresso = Math.min(
        PESO_RECEBIMENTO * conclusao.recebimento
            + PESO_DIGITALIZACAO * conclusao.digitalizacao
            + PESO_DIGITACAO * conclusao.digitacao,
        100
    );

    const largura = Math.min(Math.max(progresso, 0), 100);
    const concluido = base > 0 && progresso >= 100;

    return (
        <Card className={`${classes.card} ${concluido ? classes.cardVerde : classes.cardAmarelo}`}>
            <CardContent className={classes.content}>
                <Typography variant="overline" className={classes.titulo}>
                    Progresso do Processo{regiao ? ` para a Região ${regiao}${incluiGrupo ? ' (Incluíndo Região Grupo)' : ''}` : ''}
                </Typography>

                <Typography
                    variant="h2"
                    className={`${classes.percentual} ${concluido ? classes.percentualVerde : classes.percentualAmarelo}`}
                >
                    {formatarPercentual(progresso)}
                </Typography>

                <Typography variant="body2" className={classes.contadores}>
                    <strong>{recebidas.toLocaleString('pt-BR')}</strong> Recebidas ·{' '}
                    <strong>{digitalizadas.toLocaleString('pt-BR')}</strong> Digitalizadas ·{' '}
                    <strong>{digitadas.toLocaleString('pt-BR')}</strong> Digitadas
                    { descartadas > 0 &&
                        <> · <strong>{descartadas.toLocaleString('pt-BR')}</strong> Descartadas</>
                    }
                </Typography>

                <Box className={classes.faixa}>
                    <div className={classes.track}>
                        <div
                            className={`${classes.preenchimento} ${concluido ? classes.preenchimentoVerde : classes.preenchimentoAmarelo}`}
                            style={{ width: `${largura}%` }}
                        />
                    </div>
                </Box>

                <ButtonBase
                    className={classes.toggle}
                    onClick={() => setAberto(anterior => !anterior)}
                    aria-expanded={aberto}
                    aria-label={aberto ? 'Ocultar detalhamento por etapa' : 'Exibir detalhamento por etapa'}
                >
                    <Typography variant="caption">Detalhamento por etapa</Typography>
                    <ExpandMoreIcon fontSize="small" className={`${classes.chevron} ${aberto ? classes.chevronAberto : ''}`} />
                </ButtonBase>

                <Collapse in={aberto} timeout="auto" unmountOnExit className={classes.detalhamento}>
                    <Box className={classes.etapas}>
                        { FASES.map(fase => (
                            <div key={fase.chave} className={classes.etapa}>
                                <div className={classes.etapaCabecalho}>
                                    <Typography variant="caption" className={classes.etapaNome}>
                                        <span className={classes.ponto} style={{ backgroundColor: fase.cor }} />
                                        {fase.rotulo}
                                    </Typography>
                                    <Typography variant="caption" className={classes.etapaPeso}>
                                        peso {fase.peso}%
                                    </Typography>
                                    <Typography variant="caption" className={classes.etapaPercentual}>
                                        {formatarPercentual(conclusao[fase.chave] * 100)}
                                    </Typography>
                                </div>

                                <div className={classes.etapaTrack}>
                                    <div
                                        className={classes.etapaFill}
                                        style={{ width: `${conclusao[fase.chave] * 100}%`, backgroundImage: listras(fase.cor) }}
                                    />
                                </div>
                            </div>
                        )) }
                    </Box>
                </Collapse>

                <Box className={classes.detalhe}>
                    <Typography variant="caption" display="block" gutterBottom>
                        Esta barra mede apenas as etapas sob <strong>Responsabilidade da Regional</strong> — <strong>Receber (no Sistema da OBMEP), Digitalizar (no Aplicativo de Professores Mirim) e Digitar (no Sistema de Professores)</strong> as provas.
                    </Typography>
                    <Typography variant="caption" display="block" gutterBottom>
                        A <strong>Revisão</strong> das provas é realizada pela <strong>OBMEP Central</strong>, e só acontece para provas em que a digitação realizada pela Regional, foi divergente da leitura prévia das marcações.
                    </Typography>
                    <Typography variant="caption" display="block">
                        Para os casos em que não há divergência, a digitação é considerada correta e a prova é <strong>Finalizada</strong>.
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default ProgressoProcesso;
